import { Timestamp } from 'firebase/firestore';
import { chain, get, isString, size } from 'lodash';
import { mapActions, mapState } from 'pinia';
import Time from 'src/mixins/Time';
import { CANCELLATION_TYPES } from 'src/constants/appointments';
import { AUDIT_EVENT_KINDS } from 'src/constants/auditLog';
import { HUMANIZED_INTEGRATION_NAMES } from 'src/constants/integrations';
import { noteAuditRunTrailEvent } from 'src/domains/noteAuditor';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { units as unitsStore } from 'src/store/units';
import { findAuditTrailEvent, isSystemActor, toAuditEventKind } from 'src/utils/AuditLogUtils';

const MAX_LABEL_VALUE_LENGTH = 30;
const AUDITED_COLLECTIONS = ['sessionActivities', 'clients', 'users', 'data', 'appointments', 'tasks'];
// Features register their trail events here, so the generic trail imports none of them.
const AUDIT_TRAIL_EVENTS = [noteAuditRunTrailEvent];

export default {
  mixins: [Time],

  computed: {
    ...mapState(unitsStore, ['units']),
    ...mapState(clients, ['clients']),
    ...mapState(auth, ['userPrivilegesList', 'placesOfService', 'treatmentServices', 'cancellationReasons']),

    // Computed rather than the non-reactive instance maps the watchers below build: both lists are
    // bound lazily in created() and land after the audit table has already rendered its rows, so the
    // render has to depend on them.
    placesOfServiceMap() {
      return Object.fromEntries(
        (this.placesOfService || []).map((place) => [place.id, place.placeOfServiceName]),
      );
    },

    treatmentServicesMap() {
      return Object.fromEntries(
        (this.treatmentServices || []).map((service) => [service.id, service.serviceName]),
      );
    },

    cancellationReasonsMap() {
      return Object.fromEntries(
        (this.cancellationReasons || []).map((reason) => [reason.id, reason.cancellationReason]),
      );
    },
  },

  watch: {
    userPrivilegesList: {
      immediate: true,
      handler(list) {
        const map = {};
        (list || []).forEach((p) => { map[p.privilegeLabelId] = p.label; });
        // non-reactive instance property: won't trigger re-renders when accessed in format functions
        this.privilegesMap = map;
      },
    },
    units: {
      immediate: true,
      handler(list) {
        const map = {};
        (list || []).forEach((u) => { map[u.id] = u.unitName; });
        this.unitsMap = map;
      },
    },
  },

  created() {
    this.translateEventTypes = {
      'users.roleAndPrivilege': (value) => this.privilegesMap[value] || value,
      'clients.birthDate': (value) => (value?.toDate() ? this.getLocaleDateString(value?.toDate()) : null),
      'clients.units': (units) => {
        if (!units || !units.length) return units;
        return this.unitsMap[units[0]] || units;
      },
      'sessionActivities.placeOfServiceId': (value) => this.placesOfServiceMap[value] ?? value,
      'sessionActivities.treatmentServiceId': (serviceIds) => {
        if (!serviceIds?.length) return serviceIds;
        return serviceIds.map((id) => this.treatmentServicesMap[id] ?? id).join(', ');
      },
      'sessionActivities.addendumStatus': (value) => (value ? this.$t(`addendums.statuses.${value}`) : value),
      // A task stores who has it by login; the person is the staff record that login belongs to.
      'tasks.assignee': (uid) => this.clients.find((client) => client.type === 'staff' && client.user === uid)
        ?.clientName ?? this.$t('auditLogReports.textUnknownPerson'),
      'tasks.state': (value) => {
        const label = this.$t(`auditLogReports.taskStates.${value}`);
        return label.includes('auditLogReports.taskStates.') ? value : label;
      },
      'appointments.status': (value) => {
        const label = this.$t(`appointments.status.${value}`);
        return label.includes('appointments.status.') ? value : label;
      },
      ...Object.fromEntries(AUDIT_TRAIL_EVENTS.map(({ collection, field, formatValue }) => (
        [`${collection}.${field}`, formatValue]
      ))),
    };

    // The audit trail is not a scheduling surface, so neither list is bound by the time it opens.
    // Fire-and-forget: the ensure actions log their own bind errors, and the maps above are
    // computed, so rows re-render with names once the bind lands.
    this.ensurePlacesOfServiceBound().catch(() => {});
    this.ensureTreatmentServicesBound().catch(() => {});
  },

  methods: {
    ...mapActions(auth, ['ensurePlacesOfServiceBound', 'ensureTreatmentServicesBound']),

    // Audit log values are stored as raw ids. Resolving them to names has to happen everywhere a
    // value is rendered — the old/new value columns and the description sentence alike — or the
    // same event reads as "Place of service set to abc123" in one place and by name in the other.
    translateAuditLogValue(auditLogEvent, value) {
      if (value === null || value === undefined) {
        return value;
      }
      const translateFunction = this.translateEventTypes[`${auditLogEvent.collection}.${auditLogEvent.field}`];
      return translateFunction ? translateFunction(value, auditLogEvent) : value;
    },

    formatAuditLogValue(val, maxLength = null) {
      if (val === null) {
        return null;
      }
      if (val instanceof Timestamp) {
        return this.getLocaleString(val);
      }
      if ((maxLength !== null) && isString(val) && (size(val) > maxLength)) {
        return `${val.substring(0, maxLength)}...`;
      }
      return val;
    },

    getAuditEventFieldType(auditLogEvent) {
      switch (auditLogEvent.collection) {
        case 'sessionActivities': {
          if (['notes'].includes(auditLogEvent.field)) {
            return 'html';
          }
          break;
        }
        default:
      }
      return 'text';
    },

    getAuditEventType(auditLogEvent) {
      const { collection } = auditLogEvent;
      if (!AUDITED_COLLECTIONS.includes(collection)) {
        return '-';
      }

      return this.getAuditEventKindLabel(this.getAuditEventKind(auditLogEvent), collection);
    },

    getAuditEventKind(auditLogEvent) {
      return toAuditEventKind(auditLogEvent, AUDIT_TRAIL_EVENTS);
    },

    /**
     * Trail event kinds only when loaded or already chosen, so clinics without the feature never see them.
     *
     * @param {Object[]} auditLog
     * @param {string[]} [chosenKinds]
     * @return {string[]}
     */
    getOfferedAuditEventKinds(auditLog, chosenKinds = []) {
      const offeredKinds = new Set([
        ...auditLog.map((auditLogEvent) => this.getAuditEventKind(auditLogEvent)),
        ...chosenKinds,
      ]);

      return [
        ...Object.values(AUDIT_EVENT_KINDS),
        ...AUDIT_TRAIL_EVENTS.map(({ kind }) => kind).filter((kind) => offeredKinds.has(kind)),
      ];
    }, // getOfferedAuditEventKinds()

    getAuditEventKindLabel(kind, collection) {
      const trailEvent = AUDIT_TRAIL_EVENTS.find((event) => event.kind === kind);

      if (trailEvent) {
        return trailEvent.label();
      }

      if (kind === AUDIT_EVENT_KINDS.UPDATED) {
        return this.$t(`auditLogReports.eventTypes.${collection}`);
      }

      return this.$t(`auditLogReports.eventTypes.${kind}.${collection}`);
    }, // getAuditEventKindLabel()

    getAuditResourceType(collection) {
      if (!AUDITED_COLLECTIONS.includes(collection)) {
        return '-';
      }
      return this.$t(`auditLogReports.resourceTypes.${collection}`);
    },

    getAuditEventLabel(auditLogItem) {
      if (!AUDITED_COLLECTIONS.includes(auditLogItem.collection)) {
        return '-';
      }

      const trailEvent = findAuditTrailEvent(auditLogItem, AUDIT_TRAIL_EVENTS);

      if (trailEvent) {
        return trailEvent.describe(auditLogItem);
      }

      if (auditLogItem.resourceDeleted) {
        const overrideValue = auditLogItem?.overrideValue;
        let name;
        switch (auditLogItem.collection) {
          case 'sessionActivities':
            if (overrideValue?.sessionName) {
              return this.$t(
                'auditLogReports.events.resourceDeleted.sessionActivities.withSession',
                {
                  sessionName: overrideValue?.sessionName,
                  startTimestamp: overrideValue?.startTimestamp
                    ? this.getLocaleString(overrideValue.startTimestamp) : null,
                },
              );
            }
            return this.$t(
              'auditLogReports.events.resourceDeleted.sessionActivities.noSession',
              {
                startTimestamp: overrideValue?.startTimestamp
                  ? this.getLocaleString(overrideValue?.startTimestamp) : null,
              },
            );
          case 'clients':
            name = overrideValue?.client;
            break;
          case 'users':
            name = overrideValue?.userInstance;
            break;
          default:
            name = '';
        }

        return this.$t(`auditLogReports.events.resourceDeleted.${auditLogItem.collection}`, { name });
      }

      if (auditLogItem.resourceRecovered) {
        if (auditLogItem.collection === 'clients') {
          return this.$t('auditLogReports.events.resourceRecovered.clients', {
            name: auditLogItem.overrideValue?.client,
            channel: this.$t(`auditLogReports.restoreChannels.${auditLogItem.overrideValue?.restoredVia}`),
          });
        }

        return this.$t(`auditLogReports.events.resourceRecovered.${auditLogItem.collection}`);
      }

      if (auditLogItem.resourceAdded && auditLogItem.collection === 'clients') {
        const status = auditLogItem.overrideValue?.status;
        return this.$t('auditLogReports.events.resourceAdded.clients', {
          name: auditLogItem.overrideValue?.client,
          status: status ? this.$t(`clients.status.${status}`) : status,
        });
      }

      const oldValue = this.translateAuditLogValue(auditLogItem, auditLogItem.oldValue);
      const newValue = this.translateAuditLogValue(auditLogItem, auditLogItem.newValue);
      const oldValueFormatted = this.formatAuditLogValue(oldValue, MAX_LABEL_VALUE_LENGTH);
      const newValueFormatted = this.formatAuditLogValue(newValue, MAX_LABEL_VALUE_LENGTH);

      if (auditLogItem.collection === 'appointments' && auditLogItem.field === 'sessionActivity') {
        let description = '';
        if (auditLogItem.newValue && auditLogItem.oldValue) {
          description = this.$t('auditLogReports.events.updated.sessionActivities.linkedAppointmentChangedForSession');
        } else if (!auditLogItem.oldValue && auditLogItem.newValue) {
          description += this.$t('auditLogReports.events.updated.sessionActivities.appointmentLinked');
        } else if (auditLogItem.oldValue && !auditLogItem.newValue) {
          description += this.$t('auditLogReports.events.updated.sessionActivities.appointmentUnlinked');
        }

        let reason = '';
        if (auditLogItem.reason) {
          if (auditLogItem.reason.isClientChanged) {
            reason = `\n${this.$t('auditLogReports.events.updated.appointments.client')}`;
          }
          if (auditLogItem.reason.isUserChanged) {
            reason = `\n${this.$t('auditLogReports.events.updated.appointments.user')}`;
          }
          if (auditLogItem.reason.isStatusChanged) {
            reason = `\n${this.$t(
              'auditLogReports.events.updated.appointments.status',
              { status: auditLogItem.reason.status },
            )}`;
          }
        }
        return description + reason;
      }

      // Worded per field, as activities are: a task's fields mean nothing as raw names.
      if (['sessionActivities', 'tasks'].includes(auditLogItem.collection)) {
        // Created/removed rows have no old or new value to show, so their text is built
        // from addendumFields / addendumRemovalReason instead.
        if (auditLogItem.field === 'addendum') {
          const fields = (auditLogItem.addendumFields || [])
            .map((field) => this.$t(`auditLogReports.fields.${field}`))
            .join(', ');

          return this.$t('auditLogReports.events.added.sessionActivities.addendum', { fields });
        }

        if (auditLogItem.field === 'addendumRemoval') {
          if (!auditLogItem.addendumRemovalReason) {
            return this.$t('auditLogReports.events.added.sessionActivities.addendumRemovalNoReason');
          }

          return this.$t('auditLogReports.events.added.sessionActivities.addendumRemoval', {
            reason: this.$t(`addendums.removalReasons.${auditLogItem.addendumRemovalReason}`),
          });
        }

        if (auditLogItem.field === 'appointment') {
          if (auditLogItem.newValue && auditLogItem.oldValue) {
            return this.$t('auditLogReports.events.updated.sessionActivities.linkedAppointmentChangedForSession');
          }
          if (!auditLogItem.oldValue && auditLogItem.newValue) {
            return this.$t('auditLogReports.events.updated.sessionActivities.appointmentLinked');
          }
          if (auditLogItem.oldValue && !auditLogItem.newValue) {
            return this.$t('auditLogReports.events.updated.sessionActivities.appointmentUnlinked');
          }
        }

        if (auditLogItem.collection === 'tasks' && auditLogItem.field === 'assignee' && newValueFormatted === null) {
          return this.$t('auditLogReports.events.updated.tasks.unassigned', { oldValue: oldValueFormatted });
        }
        if (oldValueFormatted === null) {
          return this.$t(`auditLogReports.events.added.${auditLogItem.collection}.${auditLogItem.field}`, {
            newValue: newValueFormatted,
          });
        }

        return this.$t(`auditLogReports.events.updated.${auditLogItem.collection}.${auditLogItem.field}`, {
          oldValue: oldValueFormatted,
          newValue: newValueFormatted,
        });
      }

      // Undoing a cancellation clears the reason and type from the appointment, so the trigger
      // copies them onto the audit row. This is the only place they survive.
      if (auditLogItem.collection === 'appointments' && auditLogItem.field === 'status') {
        const { cancellationReason, cancellationType } = auditLogItem.overrideValue || {};
        const reason = this.cancellationReasonsMap[cancellationReason] ?? cancellationReason;

        // 'other' is the catch-all and says nothing the reason has not already said. Only the
        // attribution types are worth the extra words; the raw value stays on the audit row.
        const hasInformativeType = !!cancellationType && cancellationType !== CANCELLATION_TYPES.OTHER;
        const type = hasInformativeType
          ? (this.$tm('cancellationTypes')?.[cancellationType] ?? cancellationType)
          : null;

        const cancellation = reason && type ? `${reason} (${type})` : (reason || type);

        return this.$t('auditLogReports.events.updated.appointments.statusRestored', {
          oldValue: oldValueFormatted,
          newValue: newValueFormatted,
          cancellation: cancellation || this.$t('common.placeholderEmpty'),
        });
      }

      if (oldValueFormatted === null) {
        return this.$t(`auditLogReports.events.added.${auditLogItem.collection}`, {
          field: auditLogItem.field,
          newValue: newValueFormatted,
        });
      }

      return this.$t(`auditLogReports.events.updated.${auditLogItem.collection}`, {
        field: auditLogItem.field,
        oldValue: oldValueFormatted,
        newValue: newValueFormatted,
      });
    },

    formatAuditEventField(auditLogEvent, field, userCache) {
      const value = get(auditLogEvent, field);

      const overridedValue = get(auditLogEvent.overrideValue, field, null);
      if (overridedValue) {
        return overridedValue;
      }

      if (['createTimestamp'].includes(field)) {
        return value instanceof Timestamp
          ? this.getLocaleString(value)
          : '';
      }

      if (['field'].includes(field)) {
        if (!value) {
          return null;
        }

        const res = this.$t(`auditLogReports.fields.${value}`);

        // If we are handling changes of the field without description in i18n then use it as is
        if (res.includes('auditLogReports.fields.')) {
          return value;
        }

        return res;
      }

      if (['client'].includes(field)) {
        return chain(this.clients).find({ id: value }).get('clientName').value();
      }

      if (['user'].includes(field)) {
        if (isSystemActor(value)) {
          const updateSource = value.split('system-')[1] || value;
          // will return `Lumary` for `system-lumary`, `function` for `system-function` or `system` for `system`
          return HUMANIZED_INTEGRATION_NAMES[updateSource] || updateSource;
        }

        return userCache?.find(({ userId }) => userId === value)?.name;
      }

      if (['oldValue', 'newValue'].includes(field)) {
        return this.formatAuditLogValue(this.translateAuditLogValue(auditLogEvent, value));
      }

      return value;
    },

  },
};
